@if (loading()) {
  <div class="loading-container">
    <p-progressSpinner></p-progressSpinner>
    <p>Loading patient information...</p>
  </div>
} @else if (patient()) {
  <div class="patient-summary-container">
    <!-- Header Section -->
    <div class="header-section">
      <div class="patient-header">
        <h1 class="patient-name text-3xl font-bold tracking-tight">{{ getPatientFullName() }}</h1>
        <div class="action-buttons">
          <button type="button" class="new-visit-btn" (click)="onNewVisit()">
            + New Visit
          </button>
          <p-button 
            icon="pi pi-pencil" 
            label="Edit Patient" 
            severity="secondary"
            (onClick)="onEditPatient()">
          </p-button>
          <p-button 
            icon="pi pi-download" 
            label="Export" 
            severity="secondary"
            (onClick)="onExport()">
          </p-button>
          <p-button 
            icon="pi pi-print" 
            label="Print" 
            severity="secondary"
            (onClick)="onPrint()">
          </p-button>
        </div>
      </div>
    </div>

    <!-- Patient Information Card -->
    <div class="patient-info-section">
      <p-card class="patient-info-card">
        <div class="info-content">
          <h3 class="text-2xl font-semibold leading-none tracking-tight">Patient Information</h3>
          <p class="mrn text-muted-foreground">MRN: {{ patient()?.mrn }}</p>
          
          <div class="info-columns">
            <!-- Left Column -->
            <div class="info-column">
              <div class="info-section">
                <div class="info-header">
                  <h4 class="text-muted-foreground">Demographics</h4>
                </div>
                <div class="info-content-with-icon">
                  <i class="pi pi-user"></i>
                  <div class="info-text">
                    <p>{{ calculateAge(patient()?.dateOfBirth) }} years, {{ patient()?.sex }}</p>
                    <p>DOB: {{ formatDate(patient()?.dateOfBirth) }}</p>
                  </div>
                </div>
              </div>
              
              <div class="info-section">
                <div class="info-header">
                  <h4 class="text-muted-foreground">Contact</h4>
                </div>
                <div class="info-content-with-icon">
                  <i class="pi pi-phone"></i>
                  <div class="info-text">
                    <p>{{ patient()?.phoneNumber }}</p>
                    <p>{{ patient()?.email }}</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Middle Column -->
            <div class="info-column">
              <div class="info-section">
                <div class="info-header">
                  <h4 class="text-muted-foreground">Medical</h4>
                </div>
                <div class="info-content-with-icon">
                  <i class="pi pi-file"></i>
                  <div class="info-text">
                    <p>Status: {{ getPatientStatus() }}</p>
                    <p>Primary Physician: {{ patient()?.primaryPhysician }}</p>
                  </div>
                </div>
              </div>
              
              <div class="info-section">
                <div class="info-header">
                  <h4 class="text-muted-foreground">Appointments</h4>
                </div>
                <div class="info-content-with-icon">
                  <i class="pi pi-calendar"></i>
                  <div class="info-text">
                    <p>Last Visit: {{ getLastVisitDate() }}</p>
                    <p>Next Appointment: TBD</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Right Column -->
            <div class="info-column">
              <div class="info-section">
                <div class="info-header">
                  <h4 class="text-muted-foreground">Insurance</h4>
                </div>
                <div class="info-content-without-icon">
                  <div class="info-text">
                    <p>{{ patient()?.insuranceName }}</p>
                    <p>Policy #: {{ patient()?.insurancePolicyNumber }}</p>
                  </div>
                </div>
              </div>
              
              <div class="info-section">
                <div class="info-header">
                  <h4 class="text-muted-foreground">Allergies</h4>
                </div>
                <div class="info-content-without-icon">
                  <div class="allergies-list">
                    <span class="allergy-item">{{ patient()?.allergies }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </p-card>
    </div>

    <div class="tabbed-content">
    <div class="overview-content">
      <div class="section">
        <h3 class="text-2xl font-semibold leading-none tracking-tight">Patient Summary</h3>
        @if (visitNarrativeParagraphs().length > 0) {
          <div class="visit-narrative">
            @for (paragraph of visitNarrativeParagraphs(); track $index) {
              <p>{{ paragraph }}</p>
            }
          </div>
        } @else {
          <p class="visit-narrative-empty">No visit notes available yet.</p>
        }
      </div>

      <hr class="section-divider">

      <div class="section">
        <div class="transcriptions-content">
          <div class="transcription-header">
            <h3 class="text-2xl font-semibold leading-none tracking-tight">Transcription History</h3>
            <p class="text-muted-foreground">All transcriptions for this patient.</p>
          </div>
          <div class="transcription-table-container">
            @if (transcriptions().length > 0) {
              <p-table
                [value]="transcriptions()"
                [paginator]="true"
                [rows]="10"
                [showCurrentPageReport]="false"
                [showPageLinks]="true"
                [showFirstLastIcon]="true"
                [showJumpToPageDropdown]="false"
                class="transcription-table">
                <ng-template pTemplate="header">
                  <tr>
                    <th>ID</th>
                    <th>Date</th>
                    <th>Duration</th>
                    <th>Status</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-transcription>
                  <tr
                    class="transcription-row"
                    (click)="onViewTranscription(transcription.id)">
                    <td>{{ transcription.id }}</td>
                    <td>{{ formatDate(transcription.date) }}</td>
                    <td>{{ transcription.duration }}</td>
                    <td>
                      <div class="transcription-status-cell">
                        <p-tag
                          [value]="transcription.status"
                          [severity]="getStatusSeverity(transcription.status)"
                          [rounded]="true"
                          [ngClass]="{'needs-review-tag': transcription.status === 'Needs Review'}"
                          [style.background-color]="transcription.status === 'Needs Review' ? '#fef3c7' : null"
                          [style.color]="transcription.status === 'Needs Review' ? '#92400e' : null"
                          [style.border]="transcription.status === 'Needs Review' ? 'none' : null">
                        </p-tag>
                        @if (transcription.soapUnread) {
                          <span
                            class="soap-unread-dot patient-summary-soap-unread-dot"
                            title="SOAP not reviewed yet"
                            aria-label="SOAP notes not reviewed"></span>
                        }
                      </div>
                    </td>
                  </tr>
                </ng-template>
              </p-table>
            } @else {
              <div class="empty-state">
                <i class="pi pi-file-o empty-state-icon"></i>
                <p class="empty-state-text">No transcriptions found</p>
                <p class="empty-state-subtext">Patient transcriptions will appear here once recordings are made</p>
              </div>
            }
          </div>
        </div>
      </div>

      @if (showMedicationHistory) {
      <hr class="section-divider">

      <div class="section">
        <div class="medications-content">
          <div class="medication-header">
            <h3 class="text-2xl font-semibold leading-none tracking-tight">Medication History</h3>
          </div>
          <div class="current-medications-section">
            <h4 class="text-lg font-semibold mb-3">Current Medications</h4>
            <div class="medication-table-container">
              <p-table
                [value]="currentMedications()"
                [paginator]="currentMedications().length > 10"
                [rows]="10"
                [showCurrentPageReport]="false"
                [showPageLinks]="true"
                [showFirstLastIcon]="true"
                [showJumpToPageDropdown]="false"
                class="medication-table medication-table--current"
                styleClass="medication-table medication-table--current"
                [class.medication-table--with-start]="hasCurrentStartDates()">
                <ng-template pTemplate="header">
                  <tr>
                    <th>Medication</th>
                    <th>Dosage</th>
                    <th>Frequency</th>
                    @if (hasCurrentStartDates()) {
                      <th>Start Date</th>
                    }
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-medication>
                  <tr>
                    <td>{{ medication.medication }}</td>
                    <td>{{ medication.dosage }}</td>
                    <td>{{ medication.frequency }}</td>
                    @if (hasCurrentStartDates()) {
                      <td>{{ medication.startDate ? formatDate(medication.startDate) : '' }}</td>
                    }
                  </tr>
                </ng-template>
              </p-table>
            </div>
          </div>
          <div class="past-medications-section">
            <h4 class="text-lg font-semibold mb-3">Past Medications</h4>
            <div class="medication-table-container">
              <p-table
                [value]="pastMedications()"
                [paginator]="pastMedications().length > 10"
                [rows]="10"
                [showCurrentPageReport]="false"
                [showPageLinks]="true"
                [showFirstLastIcon]="true"
                [showJumpToPageDropdown]="false"
                class="medication-table medication-table--past"
                styleClass="medication-table medication-table--past">
                <ng-template pTemplate="header">
                  <tr>
                    <th>Medication</th>
                    <th>Dosage</th>
                    <th>Frequency</th>
                    <th>Date Range</th>
                    <th>Reason for Discontinuation</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-medication>
                  <tr>
                    <td>{{ medication.medication }}</td>
                    <td>{{ medication.dosage }}</td>
                    <td>{{ medication.frequency }}</td>
                    <td>{{ medication.dateRange }}</td>
                    <td>{{ medication.reasonForDiscontinuation }}</td>
                  </tr>
                </ng-template>
              </p-table>
            </div>
          </div>
        </div>
      </div>
      }

      @if (showUpcomingAppointments) {
      <hr class="section-divider">

      <div class="section appointments-section">
        <h3 class="text-2xl font-semibold leading-none tracking-tight">Upcoming Appointments</h3>
        <div class="appointments-list">
          @if (upcomingAppointments().length > 0) {
            @for (appointment of upcomingAppointments(); track $index) {
              <div class="appointment-item">
                <h4>{{ appointment.type }}</h4>
                <p>{{ formatDate(appointment.date) }}, {{ appointment.time }}</p>
              </div>
            }
          } @else {
            <div class="empty-state">
              <p class="empty-state-text">No upcoming appointments</p>
            </div>
          }
        </div>
      </div>
      }

      @if (!hideClinicalNotes) {
        <hr class="section-divider">
        <div class="section clinical-notes-content">
          <h3 class="text-2xl font-semibold leading-none tracking-tight">Clinical Notes</h3>
          <div class="clinical-notes-list">
            <div class="clinical-note-item" *ngFor="let note of clinicalNotes(); let last = last">
              <div class="note-content">
                <div class="note-header">
                  <div class="note-title-row">
                    <h4 class="note-title">{{ note.title }}</h4>
                    <div class="note-actions">
                      <p-button
                        label="View Full Note"
                        size="small"
                        severity="secondary"
                        [outlined]="true"
                        (onClick)="onViewClinicalNote(note.id)">
                      </p-button>
                    </div>
                  </div>
                  <div class="note-meta">
                    <span class="note-date">{{ formatDate(note.date) }}</span>
                    <span class="note-provider">{{ note.provider }}</span>
                  </div>
                </div>
                <div class="note-summary">
                  <p>{{ note.summary }}</p>
                </div>
              </div>
              <hr *ngIf="!last" class="note-separator">
            </div>
          </div>
        </div>
      }
    </div>
    </div>
  </div>

  <!-- Edit Patient Modal -->
  <app-patient-modal
    #patientModal
    [mode]="'edit'"
    [patient]="patient()"
    [visible]="editModalVisible()"
    (visibleChange)="editModalVisible.set($event)"
    (patientSaved)="onPatientUpdated($event)">
  </app-patient-modal>

} @else {
  <div class="error-container">
    <p>Patient not found.</p>
  </div>
}
